import {
  Component,
  Inject,
  Input,
  OnInit,
  Optional,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormGroup,
  FormControl,
  FormBuilder,
  Validators,
} from '@angular/forms';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheet,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { MatSelect } from '@angular/material/select';
import { Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { BoobleModalService } from 'booble-modal';
import { ReplaySubject, Subject, firstValueFrom, takeUntil, take } from 'rxjs';
import { BankAccountDto } from 'src/app/domain/models/dto/bank-account.dto';
import { BankDashDto } from 'src/app/domain/models/dto/bank.dto';
import { CorporateAccountDto } from 'src/app/domain/models/dto/corporate-account.dto';
import { InboundOrderDto } from 'src/app/domain/models/dto/inbound-order.dto';
import { SelectBankAccountComponent } from 'src/app/pages/orders/inbound/components/modals/select-bank-account/select-bank-account.component';
import { GenericBottomSheetComponent } from 'src/app/shared/components/generic-bottom-sheet/generic-bottom-sheet.component';
import { AuthenticationService } from 'src/app/shared/services';
import { BankAccountService } from 'src/app/shared/services/bank-account.service';
import { CorporateAccountService } from 'src/app/shared/services/corporate-account.service';
import { OrdersService } from 'src/app/shared/services/orders.service';

@Component({
  selector: 'app-add-bank-account',
  templateUrl: './add-bank-account.component.html',
  styleUrls: ['./add-bank-account.component.scss'],
})
export class AddBankAccountComponent implements OnInit {
  private readonly notifier: NotifierService;
  accountForm: FormGroup;
  boobleModalData: any;
  banks: BankDashDto[] = [];
  company: CorporateAccountDto;
  loading: boolean;
  isMobile = false;

  public bankFilterCtrl = new FormControl('');
  public filteredBanks: ReplaySubject<BankDashDto[]> = new ReplaySubject<
    BankDashDto[]
  >(1);

  @ViewChild('singleSelect', { static: true }) singleSelect: MatSelect;

  @ViewChild('bottomSheetContentBankAccount')
  bottomSheetContentBankAccount: TemplateRef<any>;

  protected _onDestroy = new Subject<void>();

  get companyId() {
    return +this.accountService.getCompanyId();
  }

  constructor(
    fb: FormBuilder,
    private modalService: BoobleModalService,
    private router: Router,
    private bankAccountService: BankAccountService,
    private readonly accountService: AuthenticationService,
    private corporateAccountService: CorporateAccountService,
    private ordersService: OrdersService,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) private dataBottomSheet,
    @Optional()
    private bottomSheetRef: MatBottomSheetRef<AddBankAccountComponent>,
    private bottomSheet: MatBottomSheet,

    notifierService: NotifierService
  ) {
    this.notifier = notifierService;

    this.accountForm = fb.group({
      name: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      number: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      agency: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
    });
  }

  get accountFormControl() {
    return this.accountForm.controls;
  }

  get account(): any {
    const account = this.accountForm.getRawValue();
    return account;
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 820 ? true : false;

    if (this.isMobile) {
      this.boobleModalData = this.dataBottomSheet;
    }

    firstValueFrom(this.bankAccountService.getNewBanks()).then((value) => {
      this.banks = value.data;

      this.filteredBanks.next(this.banks);
    });

    this.bankFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterBanks();
      });
  }

  changeBank(event: any) {
    this.accountForm.controls['name'].setValue(event.value);
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected setInitialValue() {
    this.filteredBanks
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.singleSelect.compareWith = (a: BankDashDto, b: BankDashDto) =>
          a && b && a.id === b.id;
      });
  }

  protected filterBanks() {
    if (!this.banks) {
      return;
    }
    let search = this.bankFilterCtrl.value;

    if (!search) {
      this.filteredBanks.next(this.banks);
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredBanks.next(
      this.banks.filter((bank) => {
        const searchLower = search.toLowerCase();
        return (
          bank.name.toLowerCase().indexOf(searchLower) > -1 ||
          bank.code.indexOf(search) > -1
        );
      })
    );
  }

  closeModal() {
    if (this.isMobile) {
      this.bottomSheet.dismiss();
    } else {
      this.modalService.dismiss(null);
    }
  }

  openSelectBankAccount(order: InboundOrderDto) {
    if (this.isMobile) {
      this.bottomSheet.open(GenericBottomSheetComponent, {
        data: { content: this.bottomSheetContentBankAccount },
      });
    } else {
      this.modalService.present(SelectBankAccountComponent, order);
    }
  }

  sendAccountInbound() {
    const account = new BankAccountDto();

    let bank = this.banks.find((item: any) => item.name === this.account.name);

    account.name = this.account.name;
    account.bank = bank.code + ' - ' + this.account.name;
    account.code = bank.code;
    account.swift = null;
    account.agency = this.account.agency;
    account.number = this.account.number;
    account.isDefault = null;
    account.countryId = null;
    account.country = null;

    this.loading = true;
    this.bankAccountService.addBankAccount(account).subscribe({
      next: (res) => {
        this.loading = false;
        this.notifier.notify(
          'success',
          'Conta bancária adicionada com sucesso!'
        );
        if (this.router.url.includes('orders')) {
          this.openSelectBankAccount(this.boobleModalData);
        } else if (this.router.url.includes('account')) {
          this.bankAccountService.updateBankAccountsView();
          this.closeModal();
        }
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }
}
